import React, { createContext, useState, useContext, useEffect } from 'react';
import { Platform } from 'react-native';
import Storage from '../utils/Storage';
import { BACKEND_URL } from '../Config/Const';

const FavoritosContext = createContext();

export const useFavoritos = () => {
  const context = useContext(FavoritosContext);
  if (!context) {
    throw new Error('useFavoritos debe ser usado dentro de FavoritosProvider');
  }
  return context;
};

export const FavoritosProvider = ({ children }) => {
  const [favoritos, setFavoritos] = useState([]);
  const [favoritosFaltantes, setFavoritosFaltantes] = useState([]);
  const [loading, setLoading] = useState(false);

  const obtenerAuthYSucursal = async () => {
    try {
      if (Platform.OS === 'web' && typeof window !== 'undefined') {
        return {
          mail: localStorage.getItem('mail') || '',
          codUsu: localStorage.getItem('codUsu') || '',
          suc: localStorage.getItem('sucursalId') || '4',
        };
      }
      return {
        mail: (await Storage.getItem('mail')) || '',
        codUsu: (await Storage.getItem('codUsu')) || '',
        suc: (await Storage.getItem('sucursalId')) || '4',
      };
    } catch (e) {
      return { mail: '', codUsu: '', suc: '4' };
    }
  };

  // Cargar favoritos al iniciar
  const cargarFavoritos = async () => {
    try {
      const { mail, codUsu, suc } = await obtenerAuthYSucursal();

      if (!mail || !codUsu) {
        setFavoritos([]);
        setFavoritosFaltantes([]);
        return { productos: [], productosFaltantes: [] };
      }

      const params = new URLSearchParams();
      params.append('mail', mail);
      params.append('codUsu', codUsu);
      params.append('suc', suc);

      const url = `${BACKEND_URL}/rest/productos/favoritos?${params.toString()}`;

      const response = await fetch(url, {
        method: 'GET',
        headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
      });

      if (!response.ok) throw new Error(`HTTP ${response.status}`);

      const text = await response.text();
      let data = null;
      try { data = JSON.parse(text); } catch {}

      const ds = data?.dsResultado || data?.response?.dsResultado?.dsResultado || null;
      const lista = Array.isArray(ds?.productos) ? ds.productos : Array.isArray(ds?.items) ? ds.items : [];
      const faltantes = Array.isArray(ds?.productosFaltantes) ? ds.productosFaltantes : [];

      setFavoritos(lista);
      setFavoritosFaltantes(faltantes);
      return { productos: lista, productosFaltantes: faltantes };
    } catch (err) {
      console.error('Error al cargar favoritos:', err);
      setFavoritos([]);
      setFavoritosFaltantes([]);
      return { productos: [], productosFaltantes: [] };
    }
  };

  // Agregar producto a favoritos
  const agregarFavorito = async (plu) => {
    try {
      setLoading(true);
      const { mail, codUsu, suc } = await obtenerAuthYSucursal();

      if (!mail || !codUsu) {
        console.error('Usuario no autenticado');
        return false;
      }

      const params = new URLSearchParams();
      params.append('mail', mail);
      params.append('codUsu', codUsu);
      params.append('suc', suc);
      params.append('plu', plu);

      const url = `${BACKEND_URL}/rest/productos/favoritos?${params.toString()}`;

      const response = await fetch(url, {
        method: 'POST',
        headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
      });

      if (!response.ok) throw new Error(`HTTP ${response.status}`);

      // Recargar la lista de favoritos
      await cargarFavoritos();
      return true;
    } catch (err) {
      console.error('Error al agregar favorito:', err);
      return false;
    } finally {
      setLoading(false);
    }
  };

  // Quitar producto de favoritos
  const quitarFavorito = async (plu) => {
    try {
      setLoading(true);
      const { mail, codUsu, suc } = await obtenerAuthYSucursal();

      if (!mail || !codUsu) {
        console.error('Usuario no autenticado');
        return false;
      }

      const params = new URLSearchParams();
      params.append('mail', mail);
      params.append('codUsu', codUsu);
      params.append('suc', suc);
      params.append('plu', plu);

      const url = `${BACKEND_URL}/rest/productos/favoritos?${params.toString()}`;

      const response = await fetch(url, {
        method: 'DELETE',
        headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
      });

      if (!response.ok) throw new Error(`HTTP ${response.status}`);

      // Recargar la lista de favoritos
      await cargarFavoritos();
      return true;
    } catch (err) {
      console.error('Error al quitar favorito:', err);
      return false;
    } finally {
      setLoading(false);
    }
  };

  // Toggle favorito (agregar si no está, quitar si está)
  const toggleFavorito = async (plu) => {
    const esFavorito = favoritos.some(fav => String(fav.plu) === String(plu));
    
    if (esFavorito) {
      return await quitarFavorito(plu);
    } else {
      return await agregarFavorito(plu);
    }
  };

  // Verificar si un producto es favorito
  const esFavorito = (plu) => {
    return favoritos.some(fav => String(fav.plu) === String(plu));
  };

  // Cargar favoritos al montar el componente
  useEffect(() => {
    cargarFavoritos();
  }, []);

  const value = {
    favoritos,
    favoritosFaltantes,
    loading,
    cargarFavoritos,
    agregarFavorito,
    quitarFavorito,
    toggleFavorito,
    esFavorito,
  };

  return (
    <FavoritosContext.Provider value={value}>
      {children}
    </FavoritosContext.Provider>
  );
};
